import { Fragment, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  addDays,
  endOfWeek,
  format,
  isSameDay,
  startOfWeek,
} from "date-fns";
import { es } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { listProfessionals } from "@/lib/api/professionals";
import { listPatients } from "@/lib/api/patients";
import { listVisits } from "@/lib/api/visits";
import { VisitDialog } from "@/components/visit-dialog";
import { PersonCombobox } from "@/components/person-combobox";
import type { Visit } from "@/lib/types";

export const Route = createFileRoute("/agenda")({
  head: () => ({
    meta: [
      { title: "Agenda · Rafael González" },
      { name: "description", content: "Calendario semanal de citas por profesional." },
    ],
  }),
  component: AgendaPage,
});

const HOURS = Array.from({ length: 13 }, (_, i) => 8 + i); // 8..20
const SLOT_HEIGHT = 56; // px per hour
const STATUS_BACKGROUND_ALPHA: Record<Visit["status"], string> = {
  scheduled: "22",
  completed: "4d",
  cancelled: "00",
};
const STATUS_DOT_OPACITY: Record<Visit["status"], number> = {
  scheduled: 0.7,
  completed: 1,
  cancelled: 0.35,
};

type VisitLayout = {
  visit: Visit;
  column: number;
  columnCount: number;
  groupVisits: Visit[];
  groupStart: number;
  groupEnd: number;
  isGroupFirst: boolean;
};

type TimedVisit = {
  visit: Visit;
  start: number;
  end: number;
};

function getInitials(...nameParts: string[]) {
  const initials = nameParts
    .flatMap((namePart) => namePart.trim().split(/\s+/))
    .filter(Boolean)
    .map((namePart) => namePart[0].toLocaleUpperCase("es"));

  return initials.length > 0 ? `${initials.join(".")}.` : "P.";
}

function layoutOverlappingVisits(visits: Visit[]): VisitLayout[] {
  const timedVisits: TimedVisit[] = visits
    .map((visit) => {
      const start = new Date(visit.start).getTime();
      return { visit, start, end: start + visit.durationMin * 60_000 };
    })
    .sort((a, b) => a.start - b.start || b.end - a.end);

  const groups: TimedVisit[][] = [];
  let currentGroup: TimedVisit[] = [];
  let currentGroupEnd = Number.NEGATIVE_INFINITY;

  for (const timedVisit of timedVisits) {
    if (currentGroup.length > 0 && timedVisit.start >= currentGroupEnd) {
      groups.push(currentGroup);
      currentGroup = [];
      currentGroupEnd = Number.NEGATIVE_INFINITY;
    }

    currentGroup.push(timedVisit);
    currentGroupEnd = Math.max(currentGroupEnd, timedVisit.end);
  }

  if (currentGroup.length > 0) groups.push(currentGroup);

  return groups.flatMap((group) => {
    const columnEnds: number[] = [];
    const positionedVisits = group.map(({ visit, start, end }) => {
      let column = columnEnds.findIndex((columnEnd) => columnEnd <= start);
      if (column === -1) column = columnEnds.length;
      columnEnds[column] = end;
      return { visit, column };
    });
    const columnCount = columnEnds.length;
    const groupVisits = group.map(({ visit }) => visit);
    const groupStart = group[0].start;
    const groupEnd = Math.max(...group.map(({ end }) => end));

    return positionedVisits.map(({ visit, column }, index) => ({
      visit,
      column,
      columnCount,
      groupVisits,
      groupStart,
      groupEnd,
      isGroupFirst: index === 0,
    }));
  });
}

function AgendaPage() {
  const [weekRef, setWeekRef] = useState(() => new Date());
  const [professionalFilter, setProfessionalFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState<Visit | null>(null);
  const [defaultStart, setDefaultStart] = useState<Date | null>(null);
  const [mobileOverlapOpen, setMobileOverlapOpen] = useState(false);
  const [mobileOverlapVisits, setMobileOverlapVisits] = useState<Visit[]>([]);

  const weekStart = startOfWeek(weekRef, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(weekRef, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const { data: visits = [] } = useQuery({
    queryKey: ["visits"],
    queryFn: () => listVisits(),
  });
  const { data: professionals = [] } = useQuery({ queryKey: ["professionals"], queryFn: () => listProfessionals() });
  const { data: patients = [] } = useQuery({ queryKey: ["patients"], queryFn: () => listPatients() });

  const profById = useMemo(() => Object.fromEntries(professionals.map((p) => [p.id, p])), [professionals]);
  const patientById = useMemo(() => Object.fromEntries(patients.map((p) => [p.id, p])), [patients]);

  const filteredVisits = professionalFilter === "all"
    ? visits
    : visits.filter((v) => v.professionalId === professionalFilter);

  const openSlot = (day: Date, hour: number) => {
    const d = new Date(day);
    d.setHours(hour, 0, 0, 0);
    setEditingVisit(null);
    setDefaultStart(d);
    setDialogOpen(true);
  };

  const openVisit = (v: Visit) => {
    setEditingVisit(v);
    setDefaultStart(null);
    setDialogOpen(true);
  };

  const openMobileOverlap = (overlappingVisits: Visit[]) => {
    setMobileOverlapVisits(overlappingVisits);
    setMobileOverlapOpen(true);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Agenda</h1>
          <p className="text-sm text-muted-foreground capitalize">
            Semana del {format(weekStart, "d 'de' MMMM", { locale: es })} al {format(weekEnd, "d 'de' MMMM yyyy", { locale: es })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PersonCombobox
            options={professionals}
            value={professionalFilter}
            onValueChange={setProfessionalFilter}
            placeholder="Todos los profesionales"
            allOption={{ value: "all", label: "Todos los profesionales" }}
            className="w-56"
          />
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={() => setWeekRef(addDays(weekRef, -7))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setWeekRef(new Date())}>Hoy</Button>
            <Button variant="outline" size="icon" onClick={() => setWeekRef(addDays(weekRef, 7))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={() => { setEditingVisit(null); setDefaultStart(null); setDialogOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" /> Nueva cita
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="grid" style={{ gridTemplateColumns: "60px repeat(7, minmax(0, 1fr))" }}>
          {/* Header */}
          <div className="border-b border-r bg-muted/30" />
          {days.map((d) => {
            const today = isSameDay(d, new Date());
            return (
              <div key={format(d, "yyyy-MM-dd")} className={`border-b p-2 text-center text-xs ${today ? "bg-primary/10 font-semibold text-primary" : "bg-muted/30 text-muted-foreground"}`}>
                <div className="uppercase">{format(d, "EEE", { locale: es })}</div>
                <div className="text-base font-semibold text-foreground">{format(d, "d")}</div>
              </div>
            );
          })}

          {/* Body */}
          <div className="border-r">
            {HOURS.map((h) => (
              <div key={h} style={{ height: SLOT_HEIGHT }} className="relative border-b pt-1 pr-2 text-right text-xs text-muted-foreground">
                {h}:00
              </div>
            ))}
          </div>

          {days.map((day) => {
            const dayVisits = filteredVisits.filter((v) => isSameDay(new Date(v.start), day));
            const positionedVisits = layoutOverlappingVisits(dayVisits);
            return (
              <div key={format(day, "yyyy-MM-dd")} className="relative border-r">
                {HOURS.map((h) => (
                  <div
                    key={h}
                    style={{ height: SLOT_HEIGHT }}
                    className="border-b transition-colors hover:bg-accent/40 cursor-pointer"
                    onClick={() => openSlot(day, h)}
                  />
                ))}
                {positionedVisits.map(({ visit: v, column, columnCount, groupVisits, groupStart, groupEnd, isGroupFirst }) => {
                  const start = new Date(v.start);
                  const minutesFromTop = (start.getHours() - HOURS[0]) * 60 + start.getMinutes();
                  if (minutesFromTop < 0) return null;
                  const top = (minutesFromTop / 60) * SLOT_HEIGHT;
                  const height = Math.max(28, (v.durationMin / 60) * SLOT_HEIGHT - 2);
                  const prof = profById[v.professionalId];
                  const patient = patientById[v.patientId];
                  const patientName = patient ? `${patient.firstName} ${patient.lastName}` : "Paciente";
                  const displayedPatientName = columnCount >= 5 && patient
                    ? getInitials(patient.firstName, patient.lastName)
                    : patientName;
                  const cancelled = v.status === "cancelled";
                  const statusColor = prof?.color ?? "#7d9b76";
                  const columnWidth = 100 / columnCount;
                  const groupStartDate = new Date(groupStart);
                  const groupMinutesFromTop = (groupStartDate.getHours() - HOURS[0]) * 60 + groupStartDate.getMinutes();
                  const groupTop = (groupMinutesFromTop / 60) * SLOT_HEIGHT;
                  const groupHeight = Math.max(28, ((groupEnd - groupStart) / 3_600_000) * SLOT_HEIGHT - 2);
                  return (
                    <Fragment key={v.id}>
                      {isGroupFirst && columnCount > 1 && (
                        <button
                          type="button"
                          aria-label={`Ver ${groupVisits.length} citas solapadas`}
                          onClick={(event) => {
                            event.stopPropagation();
                            openMobileOverlap(groupVisits);
                          }}
                          className="absolute left-1 right-1 z-10 flex items-start justify-center gap-0.5 overflow-hidden rounded-md border border-primary bg-primary/15 px-0.5 py-1 text-[10px] font-semibold text-primary shadow-sm sm:hidden"
                          style={{ top: groupTop, height: groupHeight }}
                        >
                          <CalendarDays className="h-3 w-3 shrink-0" />
                          <span>{groupVisits.length}</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openVisit(v); }}
                        className={`absolute overflow-hidden rounded-md border py-1 text-left text-xs shadow-sm transition-transform hover:scale-[1.01] ${columnCount > 1 ? "hidden sm:block" : ""} ${columnCount >= 5 ? "px-0" : "px-2"}`}
                        style={{
                          top,
                          height,
                          left: `calc(${column * columnWidth}% + 2px)`,
                          width: `calc(${columnWidth}% - 4px)`,
                          backgroundColor: cancelled ? "transparent" : `${statusColor}${STATUS_BACKGROUND_ALPHA[v.status]}`,
                          borderColor: statusColor,
                          textDecoration: cancelled ? "line-through" : undefined,
                          opacity: cancelled ? 0.6 : 1,
                        }}
                      >
                        <div className={`font-medium leading-tight ${columnCount >= 5 ? "whitespace-nowrap text-center text-[9px] tracking-tighter" : columnCount >= 3 ? "break-words text-[9px]" : columnCount === 2 ? "break-words text-[11px]" : ""}`}>
                          {columnCount === 1 && <>{format(start, "HH:mm")} · </>}
                          {displayedPatientName}
                        </div>
                        {columnCount < 5 && (
                          <div className="truncate text-[11px] text-muted-foreground">{v.reason}</div>
                        )}
                      </button>
                    </Fragment>
                  );
                })}
              </div>
            );
          })}
        </div>
      </Card>

      <Dialog open={mobileOverlapOpen} onOpenChange={setMobileOverlapOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Citas solapadas</DialogTitle>
            <DialogDescription>
              Selecciona una cita para abrirla.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {mobileOverlapVisits.map((visit) => {
              const patient = patientById[visit.patientId];
              const professional = profById[visit.professionalId];
              const visitStart = new Date(visit.start);
              const visitEnd = new Date(visitStart.getTime() + visit.durationMin * 60_000);
              return (
                <Button
                  key={visit.id}
                  type="button"
                  variant="outline"
                  className="h-auto w-full justify-start whitespace-normal px-3 py-2 text-left"
                  onClick={() => {
                    setMobileOverlapOpen(false);
                    openVisit(visit);
                  }}
                >
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{
                      backgroundColor: professional?.color ?? "#7d9b76",
                      opacity: STATUS_DOT_OPACITY[visit.status],
                    }}
                  />
                  <span className="min-w-0">
                    <span className="block font-medium">
                      {format(visitStart, "HH:mm")}–{format(visitEnd, "HH:mm")} · {patient ? `${patient.firstName} ${patient.lastName}` : "Paciente"}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {professional ? `${professional.firstName} ${professional.lastName} · ` : ""}{visit.reason}
                    </span>
                  </span>
                </Button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <VisitDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        visit={editingVisit}
        defaultStart={defaultStart}
        defaultProfessionalId={professionalFilter !== "all" ? professionalFilter : undefined}
      />
    </div>
  );
}



